import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  EstadoPainelControle,
  DadosVendas,
  DadosPagamento,
  DadosProduto,
  DadosCaixa,
  FiltrosPainelControle,
} from '../tipos';
import { servicoApi } from '../servicos/servico.api';
import type { EstadoRaiz } from './store';

/**
 * Fatia do painel de controle do Redux
 * Requisitos: 5, 8, 17
 *
 * - Dados de vendas, pagamentos, produtos e caixas
 * - Estado de carregamento e erros por endpoint
 * - Thunks para carregar dados do painel
 * - Estado de filtros (data, módulo)
 */

// ============================================================
// Utilitários
// ============================================================

const obterDataAtual = (): string => {
  return new Date().toISOString().split('T')[0];
};

// ============================================================
// Async Thunks
// ============================================================

/**
 * Thunk para carregar vendas diárias.
 */
export const carregarVendas = createAsyncThunk<
  DadosVendas,
  { idEstabelecimento: string; data: string; dataFim?: string; estaOnline: boolean; urlBase?: string; idCaixa?: number },
  { rejectValue: string }
>(
  'painelControle/carregarVendas',
  async ({ idEstabelecimento, data, dataFim, estaOnline, urlBase, idCaixa }, { rejectWithValue }) => {
    try {
      const dados = await servicoApi.obterVendasDiarias(
        idEstabelecimento,
        data,
        estaOnline,
        urlBase,
        dataFim,
        idCaixa
      );
      return dados;
    } catch (erro) {
      const mensagem = erro instanceof Error ? erro.message : 'Erro ao carregar vendas';
      return rejectWithValue(mensagem);
    }
  }
);

/**
 * Thunk para carregar detalhamento de pagamentos.
 */
export const carregarPagamentos = createAsyncThunk<
  DadosPagamento[],
  { idEstabelecimento: string; data: string; dataFim?: string; estaOnline: boolean; urlBase?: string; idCaixa?: number },
  { rejectValue: string }
>(
  'painelControle/carregarPagamentos',
  async ({ idEstabelecimento, data, dataFim, estaOnline, urlBase, idCaixa }, { rejectWithValue }) => {
    try {
      const dados = await servicoApi.obterDetalhamentoPagamentos(
        idEstabelecimento,
        data,
        estaOnline,
        urlBase,
        dataFim,
        idCaixa
      );
      return dados;
    } catch (erro) {
      const mensagem = erro instanceof Error ? erro.message : 'Erro ao carregar pagamentos';
      return rejectWithValue(mensagem);
    }
  }
);

/**
 * Thunk para carregar produtos mais vendidos.
 */
export const carregarProdutos = createAsyncThunk<
  DadosProduto[],
  { idEstabelecimento: string; data: string; dataFim?: string; estaOnline: boolean; urlBase?: string },
  { rejectValue: string }
>(
  'painelControle/carregarProdutos',
  async ({ idEstabelecimento, data, dataFim, estaOnline, urlBase }, { rejectWithValue }) => {
    try {
      const dados = await servicoApi.obterProdutosMaisVendidos(
        idEstabelecimento,
        data,
        estaOnline,
        urlBase,
        5,
        dataFim
      );
      return dados;
    } catch (erro) {
      const mensagem = erro instanceof Error ? erro.message : 'Erro ao carregar produtos';
      return rejectWithValue(mensagem);
    }
  }
);

/**
 * Thunk para carregar status dos caixas.
 */
export const carregarCaixas = createAsyncThunk<
  DadosCaixa[],
  { idEstabelecimento: string; estaOnline: boolean; urlBase?: string },
  { rejectValue: string }
>(
  'painelControle/carregarCaixas',
  async ({ idEstabelecimento, estaOnline, urlBase }, { rejectWithValue }) => {
    try {
      const dados = await servicoApi.obterStatusCaixas(
        idEstabelecimento,
        estaOnline,
        urlBase
      );
      return dados;
    } catch (erro) {
      const mensagem = erro instanceof Error ? erro.message : 'Erro ao carregar caixas';
      return rejectWithValue(mensagem);
    }
  }
);

/**
 * Thunk para carregar todos os dados do painel de uma vez.
 * Usa o estado atual de filtros e estabelecimento.
 */
export const carregarDadosPainel = createAsyncThunk<
  void,
  { idEstabelecimento: string; estaOnline: boolean; urlBase?: string },
  { state: EstadoRaiz }
>(
  'painelControle/carregarDadosPainel',
  async ({ idEstabelecimento, estaOnline, urlBase }, { dispatch, getState }) => {
    const { filtros } = getState().painelControle;
    const dataConsulta = filtros.dataInicio || filtros.data;

    await Promise.all([
      dispatch(
        carregarVendas({
          idEstabelecimento,
          data: dataConsulta,
          dataFim: filtros.dataFim,
          estaOnline,
          urlBase,
        })
      ),
      dispatch(
        carregarPagamentos({
          idEstabelecimento,
          data: dataConsulta,
          dataFim: filtros.dataFim,
          estaOnline,
          urlBase,
        })
      ),
      dispatch(
        carregarProdutos({
          idEstabelecimento,
          data: dataConsulta,
          dataFim: filtros.dataFim,
          estaOnline,
          urlBase,
        })
      ),
      dispatch(
        carregarCaixas({
          idEstabelecimento,
          estaOnline,
          urlBase,
        })
      ),
    ]);
  }
);

// ============================================================
// Estado Inicial — restaura filtros do localStorage
// ============================================================

const CHAVE_FILTROS = 'nexo_filtros_painel';

const restaurarFiltros = (): EstadoPainelControle['filtros'] => {
  try {
    const salvo = localStorage.getItem(CHAVE_FILTROS);
    if (salvo) {
      const filtros = JSON.parse(salvo);
      return {
        data: filtros.data || obterDataAtual(),
        modulo: filtros.modulo || 'todos',
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        idCaixa: filtros.idCaixa,
      };
    }
  } catch {
    // Ignora erros
  }
  return { data: obterDataAtual(), modulo: 'todos' };
};

const estadoInicial: EstadoPainelControle = {
  vendas: null,
  pagamentos: [],
  produtosMaisVendidos: [],
  caixas: [],
  filtros: restaurarFiltros(),
  carregando: false,
  carregandoVendas: false,
  carregandoPagamentos: false,
  carregandoProdutos: false,
  carregandoCaixas: false,
  erro: null,
  erroVendas: null,
  erroPagamentos: null,
  erroProdutos: null,
  erroCaixas: null,
};

// ============================================================
// Slice
// ============================================================

const fatiaPainelControle = createSlice({
  name: 'painelControle',
  initialState: estadoInicial,
  reducers: {
    definirFiltros(state, action: PayloadAction<Partial<FiltrosPainelControle>>) {
      state.filtros = { ...state.filtros, ...action.payload };
      // Persiste filtros no localStorage
      try {
        localStorage.setItem(CHAVE_FILTROS, JSON.stringify(state.filtros));
      } catch { /* ignora */ }
    },
    iniciarCarregamentoGeral(state) {
      state.carregando = true;
      state.erro = null;
    },
    // Vendas (sync actions)
    iniciarCarregamentoVendas(state) {
      state.carregandoVendas = true;
      state.erroVendas = null;
    },
    definirVendas(state, action: PayloadAction<DadosVendas>) {
      state.carregandoVendas = false;
      state.vendas = action.payload;
      state.erroVendas = null;
    },
    definirErroVendas(state, action: PayloadAction<string>) {
      state.carregandoVendas = false;
      state.erroVendas = action.payload;
    },
    // Pagamentos (sync actions)
    iniciarCarregamentoPagamentos(state) {
      state.carregandoPagamentos = true;
      state.erroPagamentos = null;
    },
    definirPagamentos(state, action: PayloadAction<DadosPagamento[]>) {
      state.carregandoPagamentos = false;
      state.pagamentos = action.payload;
      state.erroPagamentos = null;
    },
    definirErroPagamentos(state, action: PayloadAction<string>) {
      state.carregandoPagamentos = false;
      state.erroPagamentos = action.payload;
    },
    // Produtos (sync actions)
    iniciarCarregamentoProdutos(state) {
      state.carregandoProdutos = true;
      state.erroProdutos = null;
    },
    definirProdutos(state, action: PayloadAction<DadosProduto[]>) {
      state.carregandoProdutos = false;
      state.produtosMaisVendidos = action.payload;
      state.erroProdutos = null;
    },
    definirErroProdutos(state, action: PayloadAction<string>) {
      state.carregandoProdutos = false;
      state.erroProdutos = action.payload;
    },
    // Caixas (sync actions)
    iniciarCarregamentoCaixas(state) {
      state.carregandoCaixas = true;
      state.erroCaixas = null;
    },
    definirCaixas(state, action: PayloadAction<DadosCaixa[]>) {
      state.carregandoCaixas = false;
      state.caixas = action.payload;
      state.erroCaixas = null;
    },
    definirErroCaixas(state, action: PayloadAction<string>) {
      state.carregandoCaixas = false;
      state.erroCaixas = action.payload;
    },
    // Limpar dados ao trocar de estabelecimento
    limparDados(state) {
      state.vendas = null;
      state.pagamentos = [];
      state.produtosMaisVendidos = [];
      state.caixas = [];
      state.erro = null;
      state.erroVendas = null;
      state.erroPagamentos = null;
      state.erroProdutos = null;
      state.erroCaixas = null;
      state.carregando = false;
      state.carregandoVendas = false;
      state.carregandoPagamentos = false;
      state.carregandoProdutos = false;
      state.carregandoCaixas = false;
    },
  },
  extraReducers: (builder) => {
    // carregarVendas
    builder
      .addCase(carregarVendas.pending, (state) => {
        state.carregandoVendas = true;
        state.erroVendas = null;
      })
      .addCase(carregarVendas.fulfilled, (state, action) => {
        state.carregandoVendas = false;
        state.vendas = action.payload;
        state.erroVendas = null;
      })
      .addCase(carregarVendas.rejected, (state, action) => {
        state.carregandoVendas = false;
        state.erroVendas = action.payload ?? 'Erro ao carregar vendas';
      });

    // carregarPagamentos
    builder
      .addCase(carregarPagamentos.pending, (state) => {
        state.carregandoPagamentos = true;
        state.erroPagamentos = null;
      })
      .addCase(carregarPagamentos.fulfilled, (state, action) => {
        state.carregandoPagamentos = false;
        state.pagamentos = action.payload;
        state.erroPagamentos = null;
      })
      .addCase(carregarPagamentos.rejected, (state, action) => {
        state.carregandoPagamentos = false;
        state.erroPagamentos = action.payload ?? 'Erro ao carregar pagamentos';
      });

    // carregarProdutos
    builder
      .addCase(carregarProdutos.pending, (state) => {
        state.carregandoProdutos = true;
        state.erroProdutos = null;
      })
      .addCase(carregarProdutos.fulfilled, (state, action) => {
        state.carregandoProdutos = false;
        state.produtosMaisVendidos = action.payload;
        state.erroProdutos = null;
      })
      .addCase(carregarProdutos.rejected, (state, action) => {
        state.carregandoProdutos = false;
        state.erroProdutos = action.payload ?? 'Erro ao carregar produtos';
      });

    // carregarCaixas
    builder
      .addCase(carregarCaixas.pending, (state) => {
        state.carregandoCaixas = true;
        state.erroCaixas = null;
      })
      .addCase(carregarCaixas.fulfilled, (state, action) => {
        state.carregandoCaixas = false;
        state.caixas = action.payload;
        state.erroCaixas = null;
      })
      .addCase(carregarCaixas.rejected, (state, action) => {
        state.carregandoCaixas = false;
        state.erroCaixas = action.payload ?? 'Erro ao carregar caixas';
      });

    // carregarDadosPainel
    builder
      .addCase(carregarDadosPainel.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(carregarDadosPainel.fulfilled, (state) => {
        state.carregando = false;
      })
      .addCase(carregarDadosPainel.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.error.message ?? 'Erro ao carregar dados do painel';
      });
  },
});

export const {
  definirFiltros,
  iniciarCarregamentoGeral,
  iniciarCarregamentoVendas,
  definirVendas,
  definirErroVendas,
  iniciarCarregamentoPagamentos,
  definirPagamentos,
  definirErroPagamentos,
  iniciarCarregamentoProdutos,
  definirProdutos,
  definirErroProdutos,
  iniciarCarregamentoCaixas,
  definirCaixas,
  definirErroCaixas,
  limparDados,
} = fatiaPainelControle.actions;

export default fatiaPainelControle.reducer;
